import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { cn } from 'cn';
import { useProperties } from '@/features/properties/queries';
import { CategoryCreateButton } from '@/features/reference/CategoryCreateButton';
import { useCategories } from '@/features/reference/queries';
import { apiErrorMessage } from '@/lib/api';
import { formatCents, formatIsoDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TablePagination } from '@/components/ui/table-pagination';
import { usePagination } from '@/hooks/use-pagination';
import { AssignmentTabs } from './AssignmentTabs';
import { useAssignTransactionsBulk, useAssignmentQueue } from './queries';

/**
 * Raccourcis clavier (plan lot 8) : « a » sélectionne / désélectionne tout (la page affichée),
 * « Échap » vide la sélection — sans capter les touches quand le focus est dans un champ de saisie.
 */
function useQueueKeyboardShortcuts(allIds: number[], setSelected: (ids: Set<number>) => void, selected: Set<number>) {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;

      if (event.key === 'a') {
        event.preventDefault();
        setSelected(selected.size === allIds.length ? new Set() : new Set(allIds));
      } else if (event.key === 'Escape') {
        setSelected(new Set());
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [allIds, selected, setSelected]);
}

export function AssignmentQueuePage() {
  const queue = useAssignmentQueue();
  const properties = useProperties();
  const categories = useCategories();
  const bulkAssign = useAssignTransactionsBulk();

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [propertyId, setPropertyId] = useState<string>('');
  const [categoryId, setCategoryId] = useState<string>('');

  const { page, pageCount, start, end, setPage } = usePagination(queue.data?.length ?? 0);
  const pageRows = queue.data?.slice(start, end) ?? [];
  const pageIds = pageRows.map((tx) => tx.id);
  useQueueKeyboardShortcuts(pageIds, setSelected, selected);

  function toggle(id: number) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected(selected.size === pageIds.length ? new Set() : new Set(pageIds));
  }

  function handleAssign() {
    if (selected.size === 0 || !propertyId || !categoryId) return;
    bulkAssign.mutate(
      {
        transactionIds: [...selected],
        input: { propertyId: Number(propertyId), categoryId: Number(categoryId) },
      },
      {
        onSuccess: (result) => {
          toast.success(`${String(result.assigned.length)} écriture(s) validée(s)`);
          setSelected(new Set());
        },
        onError: (error) => toast.error(apiErrorMessage(error, "Échec de l'affectation")),
      },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-semibold">Affectation</h1>
      <AssignmentTabs />

      <p className="text-sm text-muted-foreground">
        Sélectionnez une ou plusieurs lignes (raccourcis : « a » tout sélectionner/désélectionner, « Échap » effacer la
        sélection), choisissez un bien et une catégorie, puis validez. Chaque validation renforce une règle apprise :
        les lignes suivantes du même noyau s'affecteront seules.
      </p>

      {queue.isPending && <p className="text-muted-foreground">Chargement…</p>}
      {queue.isError && <p className="text-destructive">Impossible de charger la file d'attente.</p>}
      {queue.data?.length === 0 && <p className="text-muted-foreground">Aucune écriture en attente d'affectation.</p>}

      {queue.data && queue.data.length > 0 && (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">Bien</span>
              <Select value={propertyId} onValueChange={setPropertyId}>
                <SelectTrigger><SelectValue placeholder="Choisir un bien" /></SelectTrigger>
                <SelectContent>
                  {properties.data?.map((property) => (
                    <SelectItem key={property.id} value={String(property.id)}>
                      {property.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">Catégorie</span>
              <div className="flex gap-2">
                <Select value={categoryId} onValueChange={setCategoryId}>
                  <SelectTrigger><SelectValue placeholder="Choisir une catégorie" /></SelectTrigger>
                  <SelectContent>
                    {categories.data?.map((category) => (
                      <SelectItem key={category.id} value={String(category.id)}>
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <CategoryCreateButton onCreated={(category) => setCategoryId(String(category.id))} />
              </div>
            </div>
            <Button
              onClick={handleAssign}
              disabled={selected.size === 0 || !propertyId || !categoryId || bulkAssign.isPending}
            >
              Valider {selected.size > 0 ? `(${String(selected.size)})` : ''}
            </Button>
          </div>

          <Card className="gap-0 overflow-hidden py-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/60 hover:bg-muted/60">
                <TableHead className="px-1.5 sm:px-2">
                  <input
                    type="checkbox"
                    aria-label="Tout sélectionner"
                    checked={selected.size === pageIds.length && pageIds.length > 0}
                    onChange={toggleAll}
                    className="size-4 rounded border-input"
                  />
                </TableHead>
                <TableHead className="px-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-2">
                  Date
                </TableHead>
                <TableHead className="px-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-2">
                  Libellé
                </TableHead>
                <TableHead className="px-1.5 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-2">
                  Montant
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageRows.map((tx) => (
                <TableRow key={tx.id} className={selected.has(tx.id) ? 'bg-accent/40' : undefined}>
                  <TableCell className="px-1.5 sm:px-2">
                    <input
                      type="checkbox"
                      aria-label={`Sélectionner l'écriture ${tx.label}`}
                      checked={selected.has(tx.id)}
                      onChange={() => toggle(tx.id)}
                      className="size-4 rounded border-input"
                    />
                  </TableCell>
                  <TableCell className="px-1.5 text-muted-foreground sm:px-2">
                    {formatIsoDate(tx.txDate)}
                  </TableCell>
                  <TableCell className="max-w-24 truncate px-1.5 sm:max-w-md sm:px-2" title={tx.label}>
                    {tx.label}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'px-1.5 text-right font-semibold tabular-nums sm:px-2',
                      tx.amountCents < 0 ? 'text-destructive' : 'text-emerald-700',
                    )}
                  >
                    {formatCents(tx.amountCents, 'EUR')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            page={page}
            pageCount={pageCount}
            start={start}
            end={end}
            total={queue.data.length}
            onPageChange={setPage}
          />
          </Card>
        </>
      )}
    </div>
  );
}
